import React, { useRef, useState } from 'react';
import { Download, Upload, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { fatigueApi } from '../data/fatigueApi';
import type { RotaImportResult } from '../data/rota';
import { qk } from '../../../../../lib/queryKeys';
import { useRota } from './RotaContext';
import { Banner, Panel, dataTable } from './ui';

const headers =
  'user_id,planned_start,planned_end,break_minutes,timezone,actual_start,actual_end,normal_weekly_hours,driving_km';
function download(name: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
export const RotaImport: React.FC = () => {
  const { clientId } = useRota();
  const cache = useQueryClient();
  const [open, setOpen] = useState(false),
    [csv, setCsv] = useState(''),
    [filename, setFilename] = useState('');
  const [result, setResult] = useState<RotaImportResult | null>(null),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  async function run(dryRun: boolean) {
    setBusy(true);
    setError('');
    try {
      const value = await fatigueApi.importRota(clientId, csv, dryRun);
      setResult(value);
      if (!dryRun && value.valid) {
        await cache.invalidateQueries({ queryKey: qk.fatigue.workforce(clientId) });
        setCsv('');
      }
    } catch (e) {
      const data = (e as { response?: { data?: RotaImportResult & { message?: string } } }).response?.data;
      if (data?.errors) setResult(data);
      else setError(data?.message || 'Could not import the rota. Please try again.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <p className='text-xs text-muted-foreground'>
          Upload a rota for the selected client. Preview and validate before saving.
        </p>
        <button
          type='button'
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className='inline-flex min-h-10 items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white'
        >
          <Upload className='h-4 w-4' />
          Import rota
        </button>
      </div>
      {open && (
        <Panel
          title='Import shift data'
          sub='CSV · up to 2,000 shifts · maximum 1 MB'
          actions={
            <button type='button' aria-label='Close rota import' onClick={() => setOpen(false)}>
              <X className='h-4 w-4' />
            </button>
          }
        >
          <div className='flex flex-col gap-4'>
            <p className='text-sm text-muted-foreground'>
              Use PEAK4 user IDs and full start/end timestamps with a UTC offset. Leave actual times blank for planned
              shifts. Importing replaces demo shifts for the workers in the file.
            </p>
            <div className='flex flex-wrap gap-3'>
              <button
                type='button'
                onClick={() => download('rota-template.csv', headers + '\n')}
                className='inline-flex min-h-9 items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-xs'
              >
                <Download className='h-4 w-4' />
                CSV template
              </button>
              <button
                type='button'
                onClick={() => {
                  setError('');
                  setBusy(true);
                  void fatigueApi
                    .getRota(clientId)
                    .then(({ workers }) =>
                      download(
                        'rota-worker-ids.csv',
                        'user_id,worker_name\n' +
                          workers
                            .map((w) => `${w.id},"${w.name.replace(/^[=+@-]/, "'$&").replace(/"/g, '""')}"`)
                            .join('\n'),
                      ),
                    )
                    .catch(() => setError('Could not download worker IDs. Please try again.'))
                    .finally(() => setBusy(false));
                }}
                disabled={busy}
                className='inline-flex min-h-9 items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-xs'
              >
                <Download className='h-4 w-4' />
                Worker IDs
              </button>
              <input
                ref={file}
                type='file'
                accept='.csv,text/csv'
                aria-label='Choose rota CSV'
                className='min-w-0 max-w-full text-sm'
                disabled={busy}
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  setResult(null);
                  setError('');
                  setCsv('');
                  if (!f) return;
                  if (f.size > 1_000_000) {
                    setError('Choose a CSV file smaller than 1 MB');
                    return;
                  }
                  setFilename(f.name);
                  setCsv(await f.text());
                }}
              />
            </div>
            <p className='text-xs text-muted-foreground'>
              Required: user_id, planned_start, planned_end. Optional: break_minutes, timezone, actual_start,
              actual_end, normal_weekly_hours, driving_km. Default timezone: Europe/London; default contracted hours:
              40/week.
            </p>
            {error && (
              <Banner tone='bad'>
                <span role='alert'>{error}</span>
              </Banner>
            )}
            {result && (
              <div aria-live='polite'>
                {result.valid ? (
                  <Banner tone='good'>
                    {result.imported
                      ? `${result.imported} shifts imported. The dashboard has been refreshed.`
                      : `${result.count} shifts validated from ${filename}. Ready to import.`}
                  </Banner>
                ) : (
                  <>
                    <Banner tone='bad'>Nothing saved. Correct the errors below and upload the CSV again.</Banner>
                    <div className={dataTable.wrap + ' mt-3 max-h-64'}>
                      <table className={dataTable.table}>
                        <thead>
                          <tr>
                            {['Row', 'Field', 'Issue'].map((h) => (
                              <th key={h} className={dataTable.th}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {result.errors.slice(0, 100).map((e, i) => (
                            <tr key={i}>
                              <td className={dataTable.td}>{e.row}</td>
                              <td className={dataTable.td}>{e.field}</td>
                              <td className={dataTable.td}>{e.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
            <div className='flex flex-wrap gap-3'>
              <button
                type='button'
                disabled={!csv || busy}
                onClick={() => void run(true)}
                className='min-h-10 rounded-lg border border-border px-4 py-2 text-sm disabled:opacity-50'
              >
                {busy ? 'Checking…' : 'Preview and validate'}
              </button>
              <button
                type='button'
                disabled={!csv || busy || !result?.valid}
                onClick={() => void run(false)}
                className='min-h-10 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-50'
              >
                Import validated shifts
              </button>
            </div>
          </div>
        </Panel>
      )}
    </div>
  );
};
